import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoicesAPI, invoiceTemplateAPI } from '../services/api';
import { FileText, Plus, ArrowRight, AlertCircle, Upload, CheckCircle } from 'lucide-react';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const templateFileInputRef = useRef(null);
  const [invoices, setInvoices] = useState([]);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [templateMessage, setTemplateMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchInvoices();
    fetchTemplateInfo();
  }, [statusFilter]);

  const fetchTemplateInfo = async () => {
    try {
      const res = await invoiceTemplateAPI.get();
      setTemplateInfo(res.data);
    } catch {
      setTemplateInfo(null);
    }
  };

  const handleTemplateUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setTemplateMessage('Please upload a PDF with form fields.');
      setTimeout(() => setTemplateMessage(''), 4000);
      return;
    }
    setTemplateUploading(true);
    setTemplateMessage('');
    try {
      await invoiceTemplateAPI.upload(file);
      await fetchTemplateInfo();
      setTemplateMessage('Template saved. We’ll use it for every invoice and fill in customer info.');
      setTimeout(() => setTemplateMessage(''), 4000);
      if (templateFileInputRef.current) templateFileInputRef.current.value = '';
    } catch (err) {
      setTemplateMessage(err.response?.data?.detail || 'Upload failed.');
      setTimeout(() => setTemplateMessage(''), 4000);
    } finally {
      setTemplateUploading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setError(null);
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await invoicesAPI.getAll(params);
      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const statusBadge = (status) => {
    const styles = {
      Draft: 'bg-slate-100 text-slate-700',
      Sent: 'bg-blue-100 text-blue-700',
      Paid: 'bg-emerald-100 text-emerald-700',
      Overdue: 'bg-amber-100 text-amber-700',
      Cancelled: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status || 'Draft'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-[#0a0a0a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-[#0a0a0a] mb-2">Error loading invoices</h3>
        <p className="text-[#737373] mb-6">{error}</p>
        <button onClick={fetchInvoices} className="px-4 py-2 bg-[#0a0a0a] text-white rounded-lg font-medium hover:bg-[#262626]">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#0a0a0a]">Invoices</h2>
          <p className="text-sm text-[#737373] mt-0.5">One template for every invoice — we fill it with customer info and you send to customers.</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-[#e5e5e5] bg-white px-3 py-2 text-sm text-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-[#0a0a0a]/20"
          >
            <option value="">All statuses</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* One template per dealer — we fill it each time, you send to customers */}
      <div className={`rounded-2xl border p-5 ${templateInfo?.has_fillable_pdf ? 'bg-emerald-50/50 border-emerald-200' : 'bg-amber-50/50 border-amber-200'}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {templateInfo?.has_fillable_pdf ? (
              <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <FileText className="w-6 h-6 text-amber-600 flex-shrink-0" />
            )}
            <div>
              <h3 className="font-semibold text-[#0a0a0a]">Invoice template</h3>
              <p className="text-sm text-[#525252] mt-0.5">
                {templateInfo?.has_fillable_pdf
                  ? 'Template set. Open any invoice to generate a filled PDF and send it to the customer.'
                  : 'Add one PDF template (with form fields). We use the same template every time and fill in customer info. Generate PDF and send to customers.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {templateMessage && (
              <span className={`text-sm ${templateMessage.includes('failed') || templateMessage.includes('Please') ? 'text-amber-700' : 'text-emerald-700'}`}>
                {templateMessage}
              </span>
            )}
            {templateInfo?.has_fillable_pdf ? (
              <button
                type="button"
                onClick={() => templateFileInputRef.current?.click()}
                disabled={templateUploading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e5e5e5] text-[#0a0a0a] rounded-lg font-medium hover:bg-[#f5f5f5] disabled:opacity-50"
              >
                {templateUploading ? 'Uploading…' : 'Change template'}
              </button>
            ) : (
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-900 rounded-lg font-medium hover:bg-amber-200 cursor-pointer disabled:opacity-50">
                <Upload className="w-4 h-4" />
                {templateUploading ? 'Uploading…' : 'Add template'}
                <input
                  ref={templateFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={handleTemplateUpload}
                  disabled={templateUploading}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
        {invoices.length === 0 ? (
          <div className="p-12 text-center text-[#737373]">
            <FileText className="w-12 h-12 mx-auto mb-4 text-[#a3a3a3]" />
            <p className="font-medium text-[#0a0a0a]">No invoices yet</p>
            <p className="text-sm mt-1">Invoices are created when you record a sale. You can also create them from Accounting.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Invoice #</th>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Client</th>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Type</th>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Total</th>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e5e5]">
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="hover:bg-[#fafafa] transition-colors"
                  >
                    <td className="px-5 py-3 font-medium text-[#0a0a0a]">{inv.invoice_number}</td>
                    <td className="px-5 py-3 text-[#525252]">{inv.client_name || '—'}</td>
                    <td className="px-5 py-3 text-[#525252]">{inv.invoice_type || '—'}</td>
                    <td className="px-5 py-3 font-medium text-[#0a0a0a]">${formatCurrency(inv.total)}</td>
                    <td className="px-5 py-3">{statusBadge(inv.status)}</td>
                    <td className="px-5 py-3 text-[#525252]">{formatDate(inv.issue_date)}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0a0a0a] hover:underline"
                      >
                        View
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
